// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The artefacts: findings.csv and disclosure-report.html.
 *
 * THE REPORT IS BUILT WITH createElement AND textContent, NEVER WITH A STRING
 * TEMPLATE. Every value in it — an author name, a comment, a paragraph of a
 * contract, a file path out of a relationship — is third-party content that
 * arrived in an untrusted file, and the report is opened from `file://`, where
 * it has an opaque origin and no CSP. Assembling it by concatenating strings
 * would make a document able to put script into the report about itself. Using
 * DOM construction means the escaping cannot be forgotten in one branch.
 *
 * The report also carries NO script, NO link and NO src — it is a single file
 * that opens on any machine, prints, and attaches to an email or a ticket
 * without dragging anything along with it.
 */

import { csvText } from './csv';
import type { Finding, Item, ScanResult } from './types';

const CSV_HEADER = [
  'finding',
  'severity',
  'part',
  'author',
  'date',
  'variant',
  'detail',
  'content',
  'is_reply_to',
];

export function findingsCsv(result: ScanResult): string {
  const rows: (string | undefined)[][] = [];
  for (const finding of result.findings) {
    if (finding.items.length === 0) continue;
    for (const item of finding.items) {
      rows.push([finding.title, finding.severity, item.part, item.author, item.date, item.variant, item.detail, item.text, undefined]);
      for (const reply of item.replies ?? []) {
        rows.push([
          finding.title,
          finding.severity,
          reply.part,
          reply.author,
          reply.date,
          reply.variant,
          reply.detail,
          reply.text,
          item.author ?? 'the comment above',
        ]);
      }
    }
  }
  return csvText(CSV_HEADER, rows);
}

/* ── the self-contained report ────────────────────────────────────────────── */

const REPORT_CSS = `
:root { color-scheme: light; }
body { margin: 0; background: #fbfaf8; color: #191a1d;
  font: 14px/1.55 -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; }
main { max-width: 900px; margin: 0 auto; padding: 32px 20px 64px; }
h1 { font-size: 24px; letter-spacing: -0.02em; margin: 0 0 4px; }
h2 { font-size: 17px; margin: 32px 0 4px; }
.sub { color: #55565c; margin: 0 0 24px; }
.lead { font-size: 18px; font-weight: 600; line-height: 1.35; margin: 0 0 8px; }
.card { border: 1px solid #eae6df; border-radius: 12px; background: #fff; padding: 20px; margin-bottom: 20px; }
.what { color: #55565c; margin: 0 0 12px; }
.fix { background: #f6f4f0; border: 1px solid #eae6df; border-radius: 6px; padding: 8px 12px; color: #55565c; margin-top: 12px; }
.item { border-top: 1px solid #eae6df; padding: 12px 0; }
.item:first-child { border-top: 0; padding-top: 0; }
.meta { font-size: 11px; color: #85868d; margin-bottom: 4px; }
.meta b { color: #55565c; }
.text { font-family: 'SF Mono', Consolas, monospace; font-size: 12px; white-space: pre-wrap;
  overflow-wrap: anywhere; background: #f6f4f0; border: 1px solid #eae6df; border-radius: 6px;
  padding: 8px 12px; margin: 0; }
.text.ins { background: #e9f5ee; border-color: #cbe4d6; }
.text.del { background: #fbecec; border-color: #f0d4d4; text-decoration: line-through; }
.reply { margin-left: 20px; border-left: 2px solid #ddd8cf; padding-left: 12px; }
.none { color: #147044; }
.foot { color: #85868d; font-size: 12px; border-top: 1px solid #eae6df; padding-top: 16px; margin-top: 32px; }
img { max-width: 100%; height: auto; border: 1px solid #eae6df; border-radius: 6px; display: block; }
figure { margin: 0 0 16px; }
figcaption { font-size: 11px; color: #85868d; margin-top: 4px; }
@media print { body { background: #fff; } .card { break-inside: avoid; } }
`;

export interface RevealImage {
  /** The media part this came from, used to match it to its finding item. */
  part: string;
  /** A PNG data URL of the whole stored image with the visible area outlined. */
  dataUrl: string;
  caption: string;
}

/**
 * Builds the report as a real Document and serialises it once at the end.
 *
 * `XMLSerializer` rather than string building, for the reason at the top of
 * this file: every value below reaches the page through `textContent`, so there
 * is exactly one escaping path and it is the browser's.
 */
export function buildReport(result: ScanResult, reveals: RevealImage[]): string {
  const doc = document.implementation.createHTMLDocument('docxray report');
  const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string) => {
    const node = doc.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  doc.documentElement.setAttribute('lang', 'en');
  const meta = doc.createElement('meta');
  meta.setAttribute('charset', 'utf-8');
  const viewport = doc.createElement('meta');
  viewport.setAttribute('name', 'viewport');
  viewport.setAttribute('content', 'width=device-width, initial-scale=1');
  const style = doc.createElement('style');
  style.textContent = REPORT_CSS;
  doc.head.append(meta, viewport, style);
  doc.title = `Disclosure report — ${result.fileName}`;

  const main = doc.createElement('main');

  main.append(el('h1', undefined, 'Disclosure report'));
  const sub = el('p', 'sub');
  sub.append(doc.createTextNode(result.fileName), doc.createElement('br'));
  sub.append(doc.createTextNode(`Produced by docxray on ${new Date().toLocaleString()}. Nothing in this document was uploaded.`));
  main.append(sub);

  const fired = result.findings.filter((f) => f.items.length > 0);
  const summary = el('div', 'card');
  summary.append(el('p', 'lead', headlineText(result)));
  if (fired.length === 0) {
    summary.append(el('p', 'what none', 'Every check below was run and none of them found anything.'));
  } else {
    const list = doc.createElement('ul');
    for (const finding of fired) {
      const li = doc.createElement('li');
      li.textContent = `${finding.title}: ${countLabel(finding)}`;
      list.append(li);
    }
    summary.append(list);
  }
  main.append(summary);

  for (const finding of result.findings) {
    const card = el('div', 'card');
    card.append(el('h2', undefined, finding.title));
    card.append(el('p', 'what', finding.what));

    if (finding.items.length === 0) {
      card.append(el('p', 'none', finding.notRecorded ?? finding.absent));
      main.append(card);
      continue;
    }

    if (finding.authors && finding.authors.length > 0) {
      card.append(el('p', 'meta', `Names recorded: ${finding.authors.join(', ')}`));
    }

    for (const item of finding.items) {
      card.append(itemNode(el, item, finding));
      for (const reply of item.replies ?? []) {
        const wrap = el('div', 'item reply');
        wrap.append(itemNode(el, reply, finding));
        card.append(wrap);
      }
    }

    if (finding.kind === 'cropped-images') {
      for (const reveal of reveals) {
        const figure = doc.createElement('figure');
        const img = doc.createElement('img');
        img.setAttribute('src', reveal.dataUrl);
        img.setAttribute('alt', reveal.caption);
        figure.append(img, el('figcaption', undefined, reveal.caption));
        card.append(figure);
      }
    }

    card.append(el('div', 'fix', `How to remove it — ${finding.fix}`));
    main.append(card);
  }

  const foot = el('div', 'foot');
  foot.append(
    doc.createTextNode(
      `Read from ${result.scannedParts.length} document parts of ${result.partNames.length} in the package. `,
    ),
  );
  foot.append(
    doc.createTextNode(
      'docxray reports what this file records. It does not judge whether the document is safe to send, and it never modifies it. Generated at docxray.benrichardson.dev.',
    ),
  );
  if (result.warnings.length > 0) {
    const warn = el('p', undefined, `Incomplete: ${result.warnings.join(' ')}`);
    foot.append(warn);
  }
  main.append(foot);

  doc.body.append(main);
  return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}

function itemNode(
  el: <K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string) => HTMLElementTagNameMap[K],
  item: Item,
  finding: Finding,
): HTMLElement {
  const wrap = el('div', 'item');
  const bits: string[] = [];
  if (item.author) bits.push(item.author);
  if (item.date) bits.push(item.date);
  if (item.detail) bits.push(item.detail);
  if (item.variant && finding.kind !== 'tracked-changes') bits.push(item.variant);
  bits.push(item.part);
  wrap.append(el('div', 'meta', bits.join(' · ')));
  if (item.text) {
    const cls =
      item.variant === 'insert' ? 'text ins' : item.variant === 'delete' || item.variant === 'move-from' ? 'text del' : 'text';
    wrap.append(el('pre', cls, item.text));
  }
  return wrap;
}

/* ── headline ─────────────────────────────────────────────────────────────── */

function countLabel(finding: Finding): string {
  const n = finding.items.length + finding.items.reduce((sum, i) => sum + (i.replies?.length ?? 0), 0);
  return `${n}`;
}

/**
 * The one sentence at the top.
 *
 * It states counts and nothing else. There is deliberately no verdict, no risk
 * score and no "safe to send" — docxray does not know who the recipient is or
 * what was agreed, and a confident wrong reassurance is the worst thing this
 * tool could produce.
 */
export function headlineText(result: ScanResult): string {
  const fired = result.findings.filter((f) => f.items.length > 0);
  if (fired.length === 0) {
    return 'None of the checks found anything in this document.';
  }

  const by = (kind: string) => result.findings.find((f) => f.kind === kind);
  const parts: string[] = [];

  const changes = by('tracked-changes');
  if (changes && changes.items.length > 0) {
    const authors = changes.authors?.length ?? 0;
    parts.push(
      `${changes.items.length} tracked change${changes.items.length === 1 ? '' : 's'}${authors > 0 ? ` by ${authors} ${authors === 1 ? 'person' : 'people'}` : ''}`,
    );
  }

  const comments = by('comments');
  if (comments && comments.items.length > 0) {
    const total = comments.items.length + comments.items.reduce((n, i) => n + (i.replies?.length ?? 0), 0);
    const resolved = comments.items.filter((i) => i.variant === 'resolved').length;
    parts.push(`${total} comment${total === 1 ? '' : 's'}${resolved > 0 ? ` (${resolved} marked resolved)` : ''}`);
  }

  const hidden = by('hidden-text');
  if (hidden && hidden.items.length > 0) parts.push(`${hidden.items.length} passage${hidden.items.length === 1 ? '' : 's'} of hidden text`);

  const crops = by('cropped-images');
  if (crops && crops.items.length > 0) {
    parts.push(`${crops.items.length} cropped photograph${crops.items.length === 1 ? '' : 's'}`);
  }

  const others = fired.filter(
    (f) => !['tracked-changes', 'comments', 'hidden-text', 'cropped-images'].includes(f.kind),
  ).length;
  if (parts.length === 0) return `This document exposes ${others} kind${others === 1 ? '' : 's'} of hidden information.`;
  if (others > 0) parts.push(`${others} other kind${others === 1 ? '' : 's'} of hidden information`);

  const last = parts.pop() as string;
  return `This document exposes ${parts.length > 0 ? `${parts.join(', ')} and ${last}` : last}.`;
}

export function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on a timer rather than immediately: Safari has been observed
  // cancelling the download when the URL is revoked in the same task.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
